import { useEffect, useRef } from 'react'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import ConversationView from '../components/chat/ConversationView'
import { connectEditor, type Editor } from '../lib/editorBridge'
import { collectToolResults } from '../lib/messageContent'
import { loadProject, restoreProject, saveIfChanged } from '../lib/studio'
import { appendToEditor, loadIntoEditor, mediaBytes, videoAdOf, type VideoAd } from '../lib/videoAd'
import { useChat } from '../providers/chat/context'

const SAVE_EVERY_MS = 3000

function threadAd(results: ReturnType<typeof collectToolResults>): VideoAd | null {
  const ads = [...results.values()].flatMap((result) => videoAdOf(result) ?? [])
  if (!ads.length) return null
  return { aspect_ratio: ads[0].aspect_ratio, clips: ads.flatMap((ad) => ad.clips) }
}

export default function EmbedStudio() {
  const { threadId = '' } = useParams()
  const { activeId, brandId, messages, loadingMessages, openThread, setClientToolRunner } = useChat()
  const editorRef = useRef<Editor | null>(null)
  const mediaRef = useRef(new Map<string, string>())
  const brandRef = useRef(brandId)
  const startedRef = useRef(false)
  const readyRef = useRef(false)

  useEffect(() => {
    brandRef.current = brandId
  }, [brandId])

  useEffect(() => {
    if (threadId && threadId !== activeId) void openThread(threadId)
  }, [threadId, activeId, openThread])

  useEffect(() => {
    if (window.parent === window) return
    const editor = connectEditor(window.parent)
    editorRef.current = editor
    setClientToolRunner(async (name, input) => {
      if (name === 'editor_inspect') return { result: await editor.call('adlovin:inspect') }
      if (name === 'editor_add_media') {
        if (!brandRef.current) throw new Error('No brand is selected.')
        const url = String(input.url)
        const { bytes, type } = await mediaBytes(brandRef.current, url)
        const fileName = String(input.name || url.split('?')[0].split('/').pop() || 'media')
        const result = (await editor.call('adlovin:import', { file: { name: fileName, type, bytes } }, [bytes])) as {
          paths?: string[]
        }
        for (const path of result.paths ?? []) mediaRef.current.set(path, url.split('?')[0])
        return { result }
      }
      return { result: await editor.call('adlovin:run', { commands: input.commands }) }
    })

    let saving = false
    const timer = setInterval(() => {
      const brand = brandRef.current
      if (!readyRef.current || saving || !brand) return
      saving = true
      saveIfChanged(editor, brand, threadId, mediaRef.current)
        .catch((error: unknown) => toast.error(error instanceof Error ? error.message : "Couldn't save the video."))
        .finally(() => {
          saving = false
        })
    }, SAVE_EVERY_MS)

    return () => {
      clearInterval(timer)
      setClientToolRunner(null)
      editor.close()
      editorRef.current = null
    }
  }, [setClientToolRunner, threadId])

  const loaded = activeId === threadId && !loadingMessages
  const ad = loaded ? threadAd(collectToolResults(messages)) : null

  useEffect(() => {
    const editor = editorRef.current
    if (!editor || !brandId || !loaded || startedRef.current) return
    startedRef.current = true
    const start = async () => {
      const saved = await loadProject(brandId, threadId)
      if (saved) {
        for (const { path, url } of saved.media ?? []) mediaRef.current.set(path, url)
        await restoreProject(editor, brandId, saved)
        const inProject = new Set(mediaRef.current.values())
        const missing = ad?.clips.filter((clip) => !inProject.has(clip.url.split('?')[0])) ?? []
        if (missing.length) {
          for (const { path, url } of await appendToEditor(editor, missing, brandId)) mediaRef.current.set(path, url)
        }
      } else if (ad) {
        for (const { path, url } of await loadIntoEditor(editor, ad, brandId, 'Video ad')) {
          mediaRef.current.set(path, url)
        }
      }
      readyRef.current = true
    }
    start().catch((error: unknown) => toast.error(error instanceof Error ? error.message : "Couldn't open the video."))
  }, [ad, brandId, loaded, threadId])

  return (
    <div className="flex h-dvh flex-col bg-white">
      <ConversationView embedded />
    </div>
  )
}
